import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Home as HomeIcon,
  Build as BuildIcon,
  ShoppingCart as CartIcon,
  Info as InfoIcon,
  Help as HelpIcon,
  History as HistoryIcon,
  Star as StarIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { login, logout, getCurrentUser, hasPermission } from './services/authService';
import { loadAndAnalyzeConfig } from './services/configLoaderService';
import CableTypeSelection from './components/CableTypeSelection';
import ConnectorSelection from './components/ConnectorSelection';
import FinishingSelection from './components/FinishingSelection';
import ReviewCart from './components/ReviewCart';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import PricingAdmin from './components/PricingAdmin';
import CompatibilityMatrixAdmin from './components/CompatibilityMatrixAdmin';
import CompatibilityAdmin from './components/CompatibilityAdmin';
import LengthSelection from './components/LengthSelection';
import QuickStartGuideDialog from './components/QuickStartGuideDialog';
import FAQDialog from './components/FAQDialog';
import SaveConfigDialog from './components/SaveConfigDialog';
import MyConfigsDialog from './components/MyConfigsDialog';
import PublicConfigsDialog from './components/PublicConfigsDialog';
import StyleSelection from './components/StyleSelection';
import ZipAudioAudioConnectorSelection from './components/ZipAudioAudioConnectorSelection';
import PanelBuilder from './components/PanelBuilder';
import CartItemsView from './components/CartItemsView';
import WireGaugeSelection from './components/WireGaugeSelection';
import CompatibilityMatrixManager from './components/CompatibilityMatrixManager';
import DiagramAdmin from './components/DiagramAdmin';
import LiveMonitoringDashboard from './components/LiveMonitoringDashboard';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import BOMViewer from './components/BOMViewer';
import CableDesignLoadingOverlay from './components/CableDesignLoadingOverlay';
import { initializeCompatibilitySystem } from './services/compatibilitySystemV2';
import { initializePricingUpdates } from './services/pricingService';
import { initializeConfigSystem, setRDSEnabled } from './services/unifiedConfigService';
import { generateEndToEndImage, captureCableConfiguration } from './services/localSvgGenerator';
import { cableImageCaptureService } from './services/cableImageCaptureService';
import { API_ENDPOINTS } from './config';
import { generateSKU } from './services/skuService';
import clientImageCaptureService from './services/clientImageCaptureService';
// import RealTimePriceEstimator from './components/RealTimePriceEstimator';
import cartService from './services/cartService';
import CartRestoreNotification from './components/CartRestoreNotification';
import analyticsService from './services/analyticsService';
import liveMonitoringService from './services/liveMonitoringService';
import { getMobileOptimizations } from './services/mobileUtils';
import mobileScrollService from './services/mobileScrollService';

// Simplified mobile imports
import { useSwipeable } from 'react-swipeable';

// Mobile optimizations CSS
import './styles/mobile-optimizations.css';

// NLFX brand colors
const nlfxColors = {
  primary: '#1a1a1a', // Dark background
  secondary: '#b32b2b', // Red accent
  text: '#ffffff',
  background: '#f5f5f5',
  card: '#ffffff',
  error: '#d32f2f',
  success: '#2e7d32'
};

function App() {
  const [activeStep, setActiveStep] = useState(0);
  const [config, setConfig] = useState({
    category: '',
    type: '',
    style: '',
    connectors: {
      endA: {
        brand: '',
        connector: '',
        gender: '',
        variant: ''
      },
      endB: {
        brand: '',
        connector: '',
        gender: '',
        variant: ''
      }
    },
    quality: '',
    finishing: {
      heatShrink: 'none',
      techFlex: 'none',
      labels: 'none'
    },
    length: 10,
    wireGauge: {
      gauge: '',
      color: 'black'
    },
    wireType: 'PSM',
    specialInstructions: '',
    hybridConnectors: undefined
  });
  const [cart, setCart] = useState(() => cartService.getCart());
  const [user, setUser] = useState(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const [configsAnchorEl, setConfigsAnchorEl] = useState(null);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [cartDialogOpen, setCartDialogOpen] = useState(false);
  const [userSavedHashes, setUserSavedHashes] = useState(() => JSON.parse(localStorage.getItem('userSavedHashes') || '[]'));
  const [myConfigsOpen, setMyConfigsOpen] = useState(false);
  const [publicConfigsOpen, setPublicConfigsOpen] = useState(false);
  const [compatibilityManagerOpen, setCompatibilityManagerOpen] = useState(false);
  const [compatibilityMatrixAdminOpen, setCompatibilityMatrixAdminOpen] = useState(false);
  const [compatibilityAdminOpen, setCompatibilityAdminOpen] = useState(false);
  const [pricingAdminOpen, setPricingAdminOpen] = useState(false);
  const [diagramAdminOpen, setDiagramAdminOpen] = useState(false);
  const [liveMonitoringOpen, setLiveMonitoringOpen] = useState(false);
  const [analyticsDashboardOpen, setAnalyticsDashboardOpen] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const [missingRequirements, setMissingRequirements] = useState([]);
  // Controls the visibility of the NEXT button tooltip when disabled
  const [nextTooltipOpen, setNextTooltipOpen] = useState(false);
  // Add state to track internal navigation state of connector selection
  const [connectorNavigationState, setConnectorNavigationState] = useState({
    step2: { isInFamilyMode: true, canGoBackToFamilies: false, handleBackToFamilies: null },
    step3: { isInFamilyMode: true, canGoBackToFamilies: false, handleBackToFamilies: null }
  });
  const svgRef = useRef(null);
  const isPopStateRef = useRef(false);
  const isFirstStepEffectRef = useRef(true);
  const scrollContainerRef = useRef(null);
  const edgeSwipeRef = useRef(null);
  const HISTORY_STATE_KEY = 'nlfx_builder_step';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mobileOpts = getMobileOptimizations();
  

  
  const [hasAddedToCart, setHasAddedToCart] = useState(false);
  const [showCartRestoreNotification, setShowCartRestoreNotification] = useState(false);
  const [hasShownRestoreNotification, setHasShownRestoreNotification] = useState(false);
  
  // Check for embedded mode from URL parameters or when inside an iframe (so scroll layout works when embedded without ?embed=true)
  const [isEmbedded, setIsEmbedded] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inIframe = typeof window !== 'undefined' && window.self !== window.top;
    return urlParams.get('embed') === 'true' || inIframe;
  });

  // Initialize mobile scroll service for embedded mode
  useEffect(() => {
    if (isEmbedded && mobileScrollService) {
      console.log('📱 [APP] Initializing mobile scroll service for embedded mode');
      // The service is already initialized in its constructor
      // We can add any additional setup here if needed
    }
  }, [isEmbedded]);

  // History API: make browser back / swipe-from-edge go to previous builder step instead of leaving the app
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return;
    const state = { [HISTORY_STATE_KEY]: 0 };
    if (!window.history.state || window.history.state[HISTORY_STATE_KEY] === undefined) {
      window.history.replaceState(state, '', window.location.href);
    }
    const onPopState = (event) => {
      const step = event.state && typeof event.state[HISTORY_STATE_KEY] === 'number' ? event.state[HISTORY_STATE_KEY] : null;
      if (step === null) return;
      isPopStateRef.current = true;
      setActiveStep(step);
      if (step === 0) {
        setConfig({
          category: '',
          type: '',
          style: '',
          connectors: { endA: { brand: '', connector: '', gender: '', variant: '' }, endB: { brand: '', connector: '', gender: '', variant: '' } },
          quality: '',
          finishing: { heatShrink: 'none', techFlex: 'none', labels: 'none' },
          length: 10,
          wireGauge: { gauge: '', color: 'black' },
          wireType: 'PSM',
          specialInstructions: '',
          hybridConnectors: undefined
        });
        setHasAddedToCart(false);
      }
      setTimeout(() => { isPopStateRef.current = false; }, 0);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.history || isPopStateRef.current) return;
    if (isFirstStepEffectRef.current) {
      isFirstStepEffectRef.current = false;
      return;
    }
    window.history.pushState({ [HISTORY_STATE_KEY]: activeStep }, '', window.location.href);
  }, [activeStep]);

  // Helper function to scroll to element with global scroll support
  const scrollToElement = useCallback((element, offset = 0) => {
    if (mobileScrollService && isEmbedded) {
      mobileScrollService.scrollToElement(element, offset);
    } else if (element) {
      // Fallback for non-embedded mode
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  }, [isEmbedded]);

  // Mobile-specific optimizations
  const [touchFeedback, setTouchFeedback] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [offlineCapable, setOfflineCapable] = useState(false);
  
  // Loading overlay state for Puppeteer capture
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const progressIntervalRef = useRef(null);


  useEffect(() => {
    // Mobile-specific loading state
    setIsLoading(true);
    
    // Restore user session on load
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
    
    // Initialize analytics service
    window.analyticsService = analyticsService;
    analyticsService.init(); // Initialize analytics tracking
    
    // Initialize live monitoring service
    window.liveMonitoringService = liveMonitoringService;
    
    // Initialize configuration systems
    
    // Initialize the unified configuration system (RDS + fallback)
    initializeConfigSystem().then(() => {
      // Initialize the legacy compatibility system for backward compatibility
      return initializeCompatibilitySystem();
    }).then(() => {
      // Initialize real-time pricing updates
      if (currentUser && hasPermission(currentUser, 'admin')) {
        const clientId = `admin-${currentUser.username}-${Date.now()}`;
        initializePricingUpdates(clientId);
      }
    }).catch((error) => {
      console.error('NLFX Cable Builder started with warnings:', error);
      setRDSEnabled(false);
    }).finally(() => {
      // Mobile-specific loading completion
      setIsLoading(false);
      
      // Mobile performance optimizations
      if (isMobile) {
        // Preload critical images for mobile
        const criticalImages = [
          '/images/nlfx_professional_laser_copy_1539721471__09702.webp',
          '/favicon.ico'
        ];
        
        preloadImages(criticalImages);
        
        // Enable touch feedback for mobile
        setTouchFeedback(true);
        
        // Check for service worker support (offline capability)
        if ('serviceWorker' in navigator) {
          setOfflineCapable(true);
        }
      }
    });
  }, [isMobile]);

  useEffect(() => {
    // Subscribe to cart changes from cartService
    const unsubscribe = cartService.subscribe((updatedCart) => {
      setCart(updatedCart);
      
      // Show restore notification only once on initial load if cart was actually restored from localStorage
      // AND there have been no fresh operations (add/remove) since page load
      if (updatedCart.length > 0 && 
          !hasShownRestoreNotification && 
          cartService.wasActuallyRestored() &&
          !cartService.hasFreshOperations()) {
        setShowCartRestoreNotification(true);
        setHasShownRestoreNotification(true);
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [hasShownRestoreNotification]);

  // Handle URL config parameter loading
  useEffect(() => {
    const loadConfigFromURL = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const configHash = urlParams.get('config');
      
      if (configHash) {
        console.log('🔗 Loading configuration from URL:', configHash);
        
        try {
          const analysis = await loadAndAnalyzeConfig(configHash);
          
          if (analysis.success) {
            console.log('✅ Config loaded successfully:', {
              cableType: analysis.cableType,
              step: analysis.step,
              componentName: analysis.componentName
            });
            
            // Update the config with the loaded configuration
            setConfig(analysis.config);
            
            // Navigate to the appropriate step
            setActiveStep(analysis.step);
            
            // If we're loading a specific cable type, we might need to update the style
            if (analysis.cableType !== 'base' && analysis.originalConfig.style) {
              setConfig(prev => ({
                ...prev,
                type: analysis.originalConfig.type || prev.type,
                style: analysis.originalConfig.style
              }));
            }
            
            // Show a notification that config was loaded
            console.log(`🎯 Loaded configuration: ${analysis.cableType} cable at step ${analysis.step}`);
          } else {
            console.warn('⚠️ Failed to load config:', analysis.error);
          }
        } catch (error) {
          console.error('❌ Error loading config from URL:', error);
        }
      }
    };
    
    loadConfigFromURL();
  }, []); // Run only once on mount

  // Handle canProceed validation for basic connector selection
  useEffect(() => {
    if (activeStep === 0) {
      // For cable type selection (step 0), require a cable type to be selected
      setCanProceed(!!config.type);
    } else if (activeStep === 1) {
      // For style selection (step 1), require a style to be selected
      setCanProceed(!!config.style);
    } else if (activeStep === 2 || activeStep === 3 || activeStep === 4) {
      // For complex connector selections (hybrid, wye, etc.), canProceed is managed by onReady callback
      if (config.style === 'hybrid' || config.style === 'zip' || config.style === 'zip-audio-audio' || 
          config.style === 'wye' || config.style === 'hybrid-power-audio-audio') {
        // These components manage their own canProceed state via onReady
        return;
      }
      
      // For basic end-to-end connector selection, let the onReady callback handle it
      // Don't override the onReady callback from ConnectorSelection
      if (activeStep === 4 && config.type === 'power') {
        // Wire gauge validation (for power cables only)
        const hasGauge = config.wireGauge?.gauge;
        const hasColor = config.wireGauge?.color;
        setCanProceed(!!(hasGauge && hasColor));
      }
    } else {
      // For other steps, allow proceeding
      setCanProceed(true);
    }
  }, [activeStep, config.wireGauge, config.style, config.type]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const user = await login(loginForm.username, loginForm.password);
      setUser(user);
      setLoginDialogOpen(false);
      setLoginForm({ username: '', password: '' });
    } catch (error) {
      setLoginError(error.message);
      
      // Track login error for live monitoring
      if (window.liveMonitoringService) {
        window.liveMonitoringService.trackError('login_error', {
          message: error.message,
          username: loginForm.username
        });
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setUser(null);
  };

  const handleNext = async () => {
    const isEndToEndStyle =
      config.style === 'end-to-end' || config.style === 'standard';
    // Power: length is step 5 (after wire gauge). Non-power: length is step 4.
    const isLeavingEndToEndLengthStep =
      isEndToEndStyle &&
      ((config.type === 'power' && activeStep === 5) ||
        (config.type !== 'power' && activeStep === 4));

    // Check if we're leaving a connector selection step (step 2 or 3)
    const isLeavingConnectorStep = activeStep === 2 || activeStep === 3;

    // End-to-end / 1:1: do not generate the diagram until length is chosen
    if (isLeavingConnectorStep && isEndToEndStyle) {
      proceedToNextStep();
      return;
    }

    const finishOverlayAndProceed = (startTime, minimumDelay, shortSuccess = false) => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setLoadingProgress(100);
      const elapsedTime = Date.now() - startTime;
      const delay = shortSuccess
        ? Math.max(1000, Math.min(3000, elapsedTime + 1000))
        : Math.max(0, minimumDelay - elapsedTime) + 500;
      setTimeout(() => {
        setShowLoadingOverlay(false);
        setLoadingProgress(0);
        proceedToNextStep();
      }, delay);
    };

    /** Generate 1:1 diagram after length is set (server first, local fallback). */
    const generateEndToEndDiagramThenProceed = async (startTime, minimumDelay) => {
      const endA = config.connectors?.endA;
      const endB = config.connectors?.endB;
      const hasConnectors =
        endA && endB && (endA.connector || endA.id) && (endB.connector || endB.id);

      if (!hasConnectors) {
        console.warn('⚠️ [APP] End-to-end length step: missing connectors, skipping diagram');
        finishOverlayAndProceed(startTime, minimumDelay);
        return;
      }

      try {
        const sku = generateSKU(config);
        const imageStyle = config.style === 'standard' ? 'standard' : 'end-to-end';
        const { resolveLengthFeetForImage } = require('./utils/cableLengthUtils');
        const lengthFeet = resolveLengthFeetForImage({ ...config, sku });
        const configWithSku = {
          ...config,
          sku,
          length: lengthFeet,
          unit: 'ft',
          wireType: config.wireType || config.audioWireType || 'PSM'
        };

        console.log('🖼️ [APP] Generating end-to-end diagram after length selection:', {
          sku,
          length: lengthFeet,
          style: imageStyle
        });

        try {
          const serverImg = await cableImageCaptureService.generateCableImage(
            configWithSku,
            imageStyle,
            { forceRegenerate: true }
          );
          if (serverImg.success && serverImg.s3Url) {
            setConfig((prev) => ({ ...prev, imageUrl: serverImg.s3Url }));
            finishOverlayAndProceed(startTime, minimumDelay, true);
            return;
          }
        } catch (serverImgErr) {
          console.warn(
            'Server-side 1:1 cable image failed, falling back to local:',
            serverImgErr
          );
        }

        let localResult;
        try {
          const pngData = await captureCableConfiguration();
          if (pngData) {
            const isS3Url = typeof pngData === 'string' && pngData.startsWith('https://');
            if (isS3Url) {
              setConfig((prev) => ({ ...prev, imageUrl: pngData }));
              finishOverlayAndProceed(startTime, minimumDelay, true);
              return;
            }
            localResult = { success: true, imageData: pngData, svgData: null };
          } else {
            throw new Error('DOM capture returned no data');
          }
        } catch (captureError) {
          console.warn('DOM capture failed, falling back to SVG generation:', captureError);
          localResult = await generateEndToEndImage({
            connectors: { endA: config.connectors.endA, endB: config.connectors.endB },
            length: lengthFeet,
            wireType: configWithSku.wireType,
            sku,
            unit: 'ft'
          });
        }

        if (localResult?.success) {
          const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_DIAGRAM, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sku,
              timestamp: Date.now(),
              imageData: localResult.imageData,
              contentType: 'image/png'
            })
          });
          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            setConfig((prev) => ({ ...prev, imageUrl: uploadData.url }));
            finishOverlayAndProceed(startTime, minimumDelay, true);
            return;
          }
          console.warn('Failed to upload end-to-end image to S3');
        } else {
          console.error('End-to-end image generation failed:', localResult?.error);
        }
      } catch (error) {
        console.error('Error during end-to-end image generation:', error);
      }

      finishOverlayAndProceed(startTime, minimumDelay);
    };

    if (isLeavingEndToEndLengthStep) {
      setShowLoadingOverlay(true);
      setLoadingProgress(0);
      const startTime = Date.now();
      const minimumDelay = 18000;

      progressIntervalRef.current = setInterval(() => {
        setLoadingProgress((prev) => Math.min(prev + Math.random() * 8 + 2, 90));
      }, 300);

      try {
        await generateEndToEndDiagramThenProceed(startTime, minimumDelay);
      } catch (error) {
        console.error('handleNext exception (end-to-end length):', error);
        finishOverlayAndProceed(startTime, minimumDelay);
      }
      return;
    }

    if (isLeavingConnectorStep) {
      // Show loading overlay (complex styles: hybrid / zip / wye — length already known)
      setShowLoadingOverlay(true);
      setLoadingProgress(0);
      
      // Start progress animation
      const startTime = Date.now();
      const minimumDelay = 18000; // 18 seconds minimum
      
      progressIntervalRef.current = setInterval(() => {
        setLoadingProgress(prev => {
          // More realistic progress for 18-second process
          // Target 90% over 18 seconds = ~5% per second
          const newProgress = Math.min(prev + Math.random() * 8 + 2, 90);
          return newProgress;
        });
      }, 300); // Slightly slower interval for smoother animation
      
      try {
        // Determine which component to capture based on config.style and config.type
        let componentId = '';
        
        if (config.style === 'wye') {
          componentId = 'wye-connector-selection';
        } else if (config.style === 'zip-audio-audio') {
          componentId = 'zip-audio-audio-connector-selection';
        } else if (config.style === 'hybrid-power-audio-audio') {
          componentId = 'hybrid-power-audio-audio-connector-selection';
        } else if (config.style === 'hybrid') {
          componentId = 'hybrid-connector-selection';
        } else if (config.style === 'zip' && config.type === 'hybrid') {
          // Handle hybrid cables that have wrong style (should be 'hybrid' but are 'zip')
          componentId = 'hybrid-connector-selection';
        }
        
        console.log('🔍 [APP] Config values:', { style: config.style, type: config.type });
        console.log('🔍 [APP] Attempting to capture image for component:', componentId);
        console.log('🔍 [APP] Registered components:', clientImageCaptureService.getRegisteredComponents());
        
        if (componentId) {
          console.log('🔍 [APP] Attempting Puppeteer capture for component:', componentId);
          
          // Generate SKU for filename
          const sku = generateSKU(config);
          
          // Call the server-side Puppeteer capture endpoint directly
          let svgElement = null;
          if (config.style === 'wye') {
            svgElement = document.getElementById('wye-cable-svg');
          } else if (config.style === 'zip-audio-audio') {
            svgElement = document.getElementById('zip-audio-audio-cable-svg');
          } else if (config.style === 'hybrid-power-audio-audio') {
            svgElement = document.getElementById('hybrid-power-audio-audio-cable-svg');
          } else if (config.style === 'hybrid' || (config.style === 'zip' && config.type === 'hybrid')) {
            svgElement = document.getElementById('hybrid-connector-selection-svg');
          }
          
          if (svgElement) {
            console.log('🔍 [APP] Found SVG element for Puppeteer capture:', svgElement.id);
            
            // Send SVG to server for Puppeteer capture
            const response = await fetch('https://nlfx-cable.luknai.com/api/capture-svg', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                svgContent: svgElement.outerHTML,
                outputFileName: `cable-${Date.now()}.png`
              })
            });
            
            console.log('🔍 [APP] Puppeteer capture response status:', response.status);
            
            if (response.ok) {
              const result = await response.json();
              console.log('🔍 [APP] Puppeteer capture result:', result);
              
              if (result.success) {
                console.log('✅ [APP] Puppeteer capture successful, updating config with imageUrl:', result.s3Url);
                setConfig(prev => ({ ...prev, imageUrl: result.s3Url }));
              
              // Capture successful - now handle the loading overlay and navigation
              
              // Complete the loading progress
              setLoadingProgress(100);
              
              // Calculate elapsed time and ensure minimum 7-second delay
              const elapsedTime = Date.now() - startTime;
              const remainingDelay = Math.max(0, minimumDelay - elapsedTime);
              
              console.log('🔍 [APP] Waiting for remaining delay before proceeding:', remainingDelay);
              
              // Wait for remaining time before hiding overlay and navigating
              setTimeout(() => {
                // Clear progress interval
                if (progressIntervalRef.current) {
                  clearInterval(progressIntervalRef.current);
                  progressIntervalRef.current = null;
                }
                
                // Hide overlay
                setShowLoadingOverlay(false);
                setLoadingProgress(0);
                
                console.log('🔍 [APP] Proceeding to next step after image capture');
                
                // Move to next step
                proceedToNextStep();
              }, remainingDelay + 500); // Add 500ms for 100% display
              
              } else {
                console.error('❌ [APP] Puppeteer capture failed:', result.error);
                
                // Capture failed - hide overlay and move to next step
                if (progressIntervalRef.current) {
                  clearInterval(progressIntervalRef.current);
                  progressIntervalRef.current = null;
                }
                
                setLoadingProgress(100);
                
                setTimeout(() => {
                  setShowLoadingOverlay(false);
                  setLoadingProgress(0);
                  
                  proceedToNextStep();
                }, 1000);
              }
            } else {
              const errorText = await response.text();
              console.error('❌ [APP] Puppeteer endpoint error:', response.status, errorText);
              
              // Endpoint error - hide overlay and move to next step
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
              
              setLoadingProgress(100);
              
              setTimeout(() => {
                setShowLoadingOverlay(false);
                setLoadingProgress(0);
                
                proceedToNextStep();
              }, 1000);
            }
          } else {
            console.warn('⚠️ [APP] No SVG element found for Puppeteer capture, proceeding without image');
            console.log('🔍 [APP] Available SVG elements:', {
              wye: document.getElementById('wye-cable-svg'),
              zip: document.getElementById('zip-audio-audio-cable-svg'),
              hybridPower: document.getElementById('hybrid-power-audio-audio-cable-svg'),
              hybrid: document.getElementById('hybrid-connector-selection-svg'),
              configStyle: config.style
            });
            finishOverlayAndProceed(startTime, minimumDelay);
          }
        } else {
          console.warn('⚠️ [APP] No complex-style component for capture, proceeding without image');
          finishOverlayAndProceed(startTime, minimumDelay);
        }
      } catch (error) {
        console.error('handleNext exception:', error);
        
        // If there was an error during capture, hide overlay and move to next step
        if (showLoadingOverlay) {
          // Clear any existing progress interval
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          
          setLoadingProgress(100);
          
          // Wait a moment to show 100% then hide overlay
          setTimeout(() => {
            setShowLoadingOverlay(false);
            setLoadingProgress(0);
            
            // Move to next step after overlay is hidden
            proceedToNextStep();
          }, 1000);
        } else {
          // If no overlay was shown, move to next step immediately
          proceedToNextStep();
        }
      }
    } else {
      // Not leaving connector or end-to-end length step, proceed normally
      proceedToNextStep();
    }
  };
  
  const proceedToNextStep = () => {
    // Track step progression for analytics
    const nextStep = (() => {
      // For hybrid/zip/wye cables, skip End B, Wire Gauge, and Length selection and go straight to Finishing
      if (activeStep === 2 && (config.style === 'hybrid' || config.style === 'zip' || config.style === 'zip-audio-audio' || config.style === 'wye' || config.style === 'hybrid-power-audio-audio')) {
        // Set the overall length from hybridConnectors before skipping to Finishing
        const overall = config.hybridConnectors?.lengths?.Overall;
        if (overall) {
          // Convert inches to feet and round to nearest inch in feet
          const lengthInFeet = Math.round(overall / 12 * 12) / 12;
          setConfig(prev => ({ ...prev, length: lengthInFeet }));
        }
        // For power hybrids, go to step 6 (Finishing), for audio hybrids go to step 5 (Finishing)
        if (config.type === 'power') {
          return 6; // Skip wire gauge, length and go to finishing for power
        } else {
          return 5; // Skip length and go to finishing for audio
        }
      }
      // --- FIX: In end-to-end mode, after connectors step, skip step 3 (End B) ---
      else if (activeStep === 2 && config.style === 'end-to-end') {
        // Do NOT clear End B, just advance to the next relevant step
        if (config.type === 'power') {
          return 4; // For power, step 4 is Wire Gauge
        } else {
          return 4; // For audio/DMX, step 4 is Length
        }
      }
      // For non-power cables, check if it's end-to-end style or complex style
      else if (activeStep === 3 && config.type !== 'power') {
        // For end-to-end style cables (one-to-one), go to Length Selection
        if (config.style === 'end-to-end') {
          return 4; // Go to length selection (step 4 for audio/DMX end-to-end)
        } else {
          // For hybrid/zip/wye cables, length is already configured, skip to finishing
          return 5; // Skip length selection and go to finishing (step 5)
        }
      }
      else {
        return activeStep + 1;
      }
    })();

    // Track step change for live monitoring
    if (window.analyticsService) {
      window.analyticsService.trackEvent('step_change', {
        fromStep: activeStep,
        toStep: nextStep,
        currentStep: nextStep,
        configType: config.type,
        configStyle: config.style
      });
    }
    
    // Track step change for live monitoring
    if (window.liveMonitoringService) {
      window.liveMonitoringService.trackStepChange(nextStep, {
        cableType: config.type,
        style: config.style,
        fromStep: activeStep
      });
    }

    setActiveStep(nextStep);
  };

  const handleBack = () => {
    // Track step regression for analytics
    const previousStep = (() => {
      // Check if we're in connector selection and can go back to families instead of previous step
      if (activeStep === 2 || activeStep === 3) {
        const currentStepKey = activeStep === 2 ? 'step2' : 'step3';
        const currentNavState = connectorNavigationState[currentStepKey];
        
        // If we're not in family mode and can go back to families, do that instead
        if (!currentNavState.isInFamilyMode && currentNavState.canGoBackToFamilies && currentNavState.handleBackToFamilies) {
          console.log('🔄 Going back to families instead of previous step');
          currentNavState.handleBackToFamilies();
          return activeStep; // Stay on current step
        }
      }
      
      // If on Wire Gauge step (step 4) for power cables, go back to Connector Selection (step 2)
      if (activeStep === 4 && config.type === 'power') {
        return 2;
      }
      // If on Length step (step 4) for non-power cables with end-to-end style, go back to Connector Selection (step 2)
      else if (activeStep === 4 && config.type !== 'power' && config.style === 'end-to-end') {
        return 2;
      }
      // If on Finishing step and coming from hybrid/zip/wye, go back to connector selection (step 2)
      else if ((activeStep === 5 || activeStep === 6) && (config.style === 'hybrid' || config.style === 'zip' || config.style === 'zip-audio-audio' || config.style === 'wye' || config.style === 'hybrid-power-audio-audio')) {
        return 2;
      }
      // For non-power cables going back from Finishing (step 5)
      else if (activeStep === 5 && config.type !== 'power') {
        // For end-to-end style, go back to Length Selection (step 4)
        if (config.style === 'end-to-end') {
          return 4; // Go back to length selection (step 4)
        } else {
          // For hybrid/zip/wye cables, go back to Connector Selection (step 3)
          return 3; // Go back to connector selection (step 3)
        }
      }
      // If on Connector Selection (step 2) and it's a Power or DMX cable with end-to-end style, go back to Cable Type (step 0)
      else if (activeStep === 2 && (config.type === 'power' || config.type === 'dmx') && config.style === 'end-to-end') {
        return 0;
      } else {
        return activeStep - 1;
      }
    })();

    // Track step change for analytics (before navigation)
    if (window.analyticsService && previousStep !== activeStep) {
      window.analyticsService.trackEvent('step_change', {
        fromStep: activeStep,
        toStep: previousStep,
        currentStep: previousStep,
        configType: config.type,
        configStyle: config.style,
        direction: 'back'
      });
    }
    if (window.liveMonitoringService && previousStep !== activeStep) {
      window.liveMonitoringService.trackStepChange(previousStep, {
        cableType: config.type,
        style: config.style,
        fromStep: activeStep,
        direction: 'back'
      });
    }

    // Use browser history so back/swipe-from-edge and in-app Back stay in sync; popstate will set step and clear config when step 0
    if (typeof window !== 'undefined' && window.history && window.history.state && window.history.state[HISTORY_STATE_KEY] !== undefined) {
      window.history.back();
      return;
    }
    setActiveStep(previousStep);
    if (previousStep === 0) {
      setConfig({
        category: '',
        type: '',
        style: '',
        connectors: {
          endA: { brand: '', connector: '', gender: '', variant: '' },
          endB: { brand: '', connector: '', gender: '', variant: '' }
        },
        quality: '',
        finishing: { heatShrink: 'none', techFlex: 'none', labels: 'none' },
        length: 10,
        wireGauge: { gauge: '', color: 'black' },
        wireType: 'PSM',
        specialInstructions: '',
        hybridConnectors: undefined
      });
      setHasAddedToCart(false);
    }
  };

  const handleStepClick = (stepIndex) => {
    // Only allow clicking on completed steps (steps that have been visited)
    if (stepIndex < activeStep) {
      // If clicking on Cable Type (step 0), reset everything
      if (stepIndex === 0) {
        setActiveStep(0);
        setConfig({
          category: '',
          type: '',
          style: '',
          connectors: {
            endA: {
              brand: '',
              connector: '',
              gender: '',
              variant: ''
            },
            endB: {
              brand: '',
              connector: '',
              gender: '',
              variant: ''
            }
          },
          quality: '',
          finishing: {
            heatShrink: 'none',
            techFlex: 'none',
            labels: 'none'
          },
          length: 10,
          wireGauge: {
            gauge: '',
            color: 'black'
          },
          wireType: 'PSM',
          specialInstructions: '',
          hybridConnectors: undefined
        });
        setHasAddedToCart(false);
        setCanProceed(false);
        setMissingRequirements([]);
        setConnectorNavigationState({
          step2: { isInFamilyMode: true, canGoBackToFamilies: false, handleBackToFamilies: null },
          step3: { isInFamilyMode: true, canGoBackToFamilies: false, handleBackToFamilies: null }
        });
        setNextTooltipOpen(false);
      } else {
        setActiveStep(stepIndex);
      }
    }
  };

  // Simplified mobile swipe gesture handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (canProceed && ((config.type === 'power' && activeStep < 7) || (config.type !== 'power' && activeStep < 6))) {
        handleNext();
      }
    },
    onSwipedRight: () => {
      if (activeStep > 0) {
        handleBack();
      }
    },
    // Simplified options for better compatibility
    preventDefaultTouchmoveEvent: false,
    trackMouse: false,
    delta: 50,
    swipeDuration: 500
  });

  // When embedded on mobile: detect swipe-from-right-edge (start right, move left) to go back
  useEffect(() => {
    if (!isEmbedded || !isMobile || typeof handleBack !== 'function') return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const EDGE_WIDTH = 24;
    const MIN_SWIPE = 50;
    const onStart = (e) => {
      if (e.touches?.[0]) {
        const x = e.touches[0].clientX;
        const rightEdge = typeof window !== 'undefined' ? window.innerWidth - EDGE_WIDTH : 0;
        if (x > rightEdge) edgeSwipeRef.current = { x };
      }
    };
    const onEnd = (e) => {
      const start = edgeSwipeRef.current;
      edgeSwipeRef.current = null;
      if (!start || !e.changedTouches?.[0] || activeStep === 0) return;
      const endX = e.changedTouches[0].clientX;
      if (start.x - endX > MIN_SWIPE) handleBack(); // moved left
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [isEmbedded, isMobile, activeStep, handleBack]);

  // Simplified mobile image preloading
  const preloadImages = (imageUrls) => {
    // Simple image preloading without complex observers
    imageUrls.forEach(src => {
      const img = new Image();
      img.src = src;
    });
    setImagesLoaded(true);
  };

  // Remove complex lazy loading setup
  useEffect(() => {
    // Simple mobile detection for basic optimizations
    if (isMobile) {
      // Set images as loaded after a short delay
      setTimeout(() => setImagesLoaded(true), 1000);
    }
  }, [isMobile]);

  const handleReset = () => {
    setActiveStep(0);
    setConfig({
      category: '',
      type: '',
      style: '',
      connectors: {
        endA: {
          brand: '',
          connector: '',
          gender: '',
          variant: ''
        },
        endB: {
          brand: '',
          connector: '',
          gender: '',
          variant: ''
        }
      },
      quality: '',
      finishing: {
        heatShrink: 'none',
        techFlex: 'none',
        labels: 'none'
      },
      length: 10,
      wireGauge: {
        gauge: '',
        color: 'black'
      },
      wireType: 'PSM',
      specialInstructions: '',
      hybridConnectors: undefined
    });
    setHasAddedToCart(false);
  };

  const handleAddToCart = (item) => {
    cartService.addToCart(item);
    setHasAddedToCart(true);
    
    // Track cart addition for live monitoring
    if (window.liveMonitoringService) {
      window.liveMonitoringService.trackCartAddition(item, item.price, item.sku);
    }
  };

  const handleRemoveFromCart = (index) => {
    cartService.removeFromCart(index);
  };

  const handleClearCart = () => {
    cartService.clearCart();
  };

  const handleMissingRequirements = (requirements) => {
    setMissingRequirements(requirements);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <CableTypeSelection
            onSelect={(typeObj) => {
              // Track cable type selection
              if (window.analyticsService) {
                window.analyticsService.trackEvent('cable_type_selected', {
                  cableType: typeObj.type,
                  style: typeObj.style,
                  currentStep: 0
                });
              }
              
              // Track cable type selection for live monitoring
              if (window.liveMonitoringService) {
                window.liveMonitoringService.trackStepChange(1, {
                  cableType: typeObj.type,
                  style: typeObj.style,
                  fromStep: 0
                });
              }
              
              // Clear all configuration state when selecting a new cable type
              // This prevents data from one cable type bleeding into another
              setConfig({
                category: '',
                type: typeObj.type,
                style: typeObj.style,
                connectors: {
                  endA: { brand: '', connector: '', gender: '', variant: '' },
                  endB: { brand: '', connector: '', gender: '', variant: '' }
                },
                quality: '',
                finishing: {
                  heatShrink: 'none',
                  techFlex: 'none',
                  labels: 'none'
                },
                length: 10,
                wireGauge: {
                  gauge: '',
                  color: 'black'
                },
                wireType: typeObj.type === 'dmx' ? 'PSDMX' : 'PSM',
                specialInstructions: '',
                // Completely clear hybridConnectors to prevent cross-contamination
                hybridConnectors: undefined
              });
              
              // Reset navigation state when switching cable types
              setConnectorNavigationState({
                step2: { isInFamilyMode: true, canGoBackToFamilies: false, handleBackToFamilies: null },
                step3: { isInFamilyMode: true, canGoBackToFamilies: false, handleBackToFamilies: null }
              });
              
              // Reset other state
              setCanProceed(false);
              setMissingRequirements([]);
              setNextTooltipOpen(false);
              
              // If Power or DMX cable with style is selected, skip Style Selection (step 1) and go to step 2
              if ((typeObj.type === 'power' || typeObj.type === 'dmx') && typeObj.style) {
                setActiveStep(2);
              } else {
                handleNext();
              }
            }}
          />
        );
      case 1:
        return (
          <StyleSelection
            value={config.style}
            onChange={(style) => {
              // Track style selection
              if (window.analyticsService) {
                window.analyticsService.trackEvent('style_selected', {
                  style,
                  cableType: config.type,
                  currentStep: 1
                });
              }
              
              // Clear hybridConnectors when style changes to ensure fresh start
              setConfig({ ...config, style, hybridConnectors: undefined, wireType: config.wireType, specialInstructions: config.specialInstructions });
              
              // Reset navigation state when switching styles
              setConnectorNavigationState({
                step2: { isInFamilyMode: true, canGoBackToFamilies: false, handleBackToFamilies: null },
                step3: { isInFamilyMode: true, canGoBackToFamilies: false, handleBackToFamilies: null }
              });
              
              // Reset other state
              setCanProceed(false);
              setMissingRequirements([]);
              setNextTooltipOpen(false);
              
              
              handleNext();
            }}
            onNext={handleNext}
            onBack={handleBack}
            cableType={config.type}
          />
        );
      case 2:
        if (config.style === 'hybrid' || config.style === 'zip') {
          const HybridConnectorSelection = require('./components/HybridConnectorSelection').default;
          return (
            <HybridConnectorSelection
              value={config.hybridConnectors}
              onChange={(val) => setConfig({ 
                ...config, 
                hybridConnectors: val,
                imageUrl: val.imageUrl // Also set imageUrl at top level for ReviewCart access
              })}
              onNext={handleNext}
              onBack={handleBack}
              onReady={setCanProceed}
              onMissingRequirements={handleMissingRequirements}
            />
          );
        }
        if (config.style === 'zip-audio-audio') {
          return (
            <ZipAudioAudioConnectorSelection
              value={config.hybridConnectors}
              onChange={(val) => setConfig({ 
                ...config, 
                hybridConnectors: val,
                imageUrl: val.imageUrl // Also set imageUrl at top level for ReviewCart access
              })}
              onNext={handleNext}
              onBack={handleBack}
              onReady={setCanProceed}
              onMissingRequirements={handleMissingRequirements}
            />
          );
        }
        if (config.style === 'wye' && config.type === 'audio') {
          const WyeConnectorSelection = require('./components/WyeConnectorSelection').default;
          return (
            <WyeConnectorSelection
              value={config.hybridConnectors}
              onChange={(val) => setConfig({ 
                ...config, 
                hybridConnectors: val,
                imageUrl: val.imageUrl // Also set imageUrl at top level for ReviewCart access
              })}
              connectorType="audio"
              onNext={handleNext}
              onBack={handleBack}
              onReady={setCanProceed}
              onMissingRequirements={handleMissingRequirements}
            />
          );
        }
        if (config.style === 'wye' && config.type === 'power') {
          const WyeConnectorSelection = require('./components/WyeConnectorSelection').default;
          return (
            <WyeConnectorSelection
              value={config.hybridConnectors}
              onChange={(val) => setConfig({ 
                ...config, 
                hybridConnectors: val,
                imageUrl: val.imageUrl // Also set imageUrl at top level for ReviewCart access
              })}
              connectorType="power"
              onNext={handleNext}
              onBack={handleBack}
              onReady={setCanProceed}
              onMissingRequirements={handleMissingRequirements}
            />
          );
        }
        if (config.style === 'hybrid-power-audio-audio') {
          const HybridPowerAudioAudioConnectorSelection = require('./components/HybridPowerAudioAudioConnectorSelection').default;
          return (
            <HybridPowerAudioAudioConnectorSelection
              value={config.hybridConnectors}
              onChange={(val) => setConfig({ 
                ...config, 
                hybridConnectors: val,
                imageUrl: val.imageUrl // Also set imageUrl at top level for ReviewCart access
              })}
              onNext={handleNext}
              onBack={handleBack}
              onReady={setCanProceed}
              onMissingRequirements={handleMissingRequirements}
            />
          );
        }
        // For end-to-end style, show centered header/subheader above selector
        if (config.style === 'end-to-end') {
          return (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Select Connectors for End to End
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Choose the connectors that will be attached to each end of your cable.
                </Typography>
              </Box>
              <ConnectorSelection
                value={{ endA: config.connectors.endA, endB: config.connectors.endB, wireType: config.wireType, specialInstructions: config.specialInstructions }}
                onChange={(connectorData) => {
                  setConfig({
                    ...config,
                    connectors: {
                      ...config.connectors,
                      endA: connectorData.endA,
                      endB: connectorData.endB
                    },
                    wireType: connectorData.wireType,
                    specialInstructions: connectorData.specialInstructions
                  });
                }}
                type={config.type}
                onNext={handleNext}
                onBack={handleBack}
                onReady={setCanProceed}
                onMissingRequirements={handleMissingRequirements}
              />
            </>
          );
        }
        break;
      case 3:
        // --- FIX: In end-to-end mode, skip rendering step 3 (old End B selection) ---
        if (config.style === 'end-to-end') {
          // Skip this step entirely
          return null;
        }
        return (
          <ConnectorSelection
            value={config.connectors.endB}
            onChange={(connector) => {
              setConfig({
                ...config,
                connectors: {
                  ...config.connectors,
                  endB: connector
                }
              });
            }}
            onNext={handleNext}
            onBack={handleBack}
            end="B"
            type={config.type}
            otherConnector={config.connectors.endA}
            showCompatibility={true}
            onReady={setCanProceed}
            onMissingRequirements={handleMissingRequirements}
            onNavigationStateChange={(navState) => {
              setConnectorNavigationState(prev => ({
                ...prev,
                step3: navState
              }));
            }}
            onResetAll={() => {
              setConfig({
                ...config,
                connectors: {
                  endA: { brand: '', connector: '', gender: '', variant: '' },
                  endB: { brand: '', connector: '', gender: '', variant: '' }
                },
                wireType: config.wireType,
                specialInstructions: config.specialInstructions
              });
            }}
          />
        );
      case 4:
        // Wire Gauge selection (for power cables only)
        if (config.type === 'power') {
          return (
            <WireGaugeSelection
              value={config.wireGauge}
              onChange={(wireGauge) => setConfig({ ...config, wireGauge })}
              connectors={config.connectors}
            />
          );
        } else {
          // For non-power cables, go to Length Selection
          // If coming from hybrid/zip, prepopulate and disable overall length
          let lengthValue = config.length;
          let disableLength = false;
          if (config.style === 'hybrid' || config.style === 'zip' || config.style === 'zip-audio-audio' || config.style === 'wye' || config.style === 'hybrid-power-audio-audio') {
            // Use overall length from hybridConnectors if available
            const overall = config.hybridConnectors?.lengths?.Overall;
            if (overall) {
              // Convert inches to feet and round to nearest inch in feet
              lengthValue = Math.round(overall / 12 * 12) / 12;
              disableLength = true;
            }
          }
          return (
            <LengthSelection
              value={lengthValue}
              onChange={(length) =>
                setConfig({ ...config, length, imageUrl: undefined })
              }
              onNext={handleNext}
              onBack={handleBack}
              disableLength={disableLength}
            />
          );
        }
      case 5:
        // Length Selection (for power cables after wire gauge)
        if (config.type === 'power') {
          // If coming from hybrid/zip, prepopulate and disable overall length
          let lengthValue = config.length;
          let disableLength = false;
          if (config.style === 'hybrid' || config.style === 'zip' || config.style === 'zip-audio-audio' || config.style === 'wye' || config.style === 'hybrid-power-audio-audio') {
            // Use overall length from hybridConnectors if available
            const overall = config.hybridConnectors?.lengths?.Overall;
            if (overall) {
              // Convert inches to feet and round to nearest inch in feet
              lengthValue = Math.round(overall / 12 * 12) / 12;
              disableLength = true;
            }
          }
          return (
            <LengthSelection
              value={lengthValue}
              onChange={(length) =>
                setConfig({ ...config, length, imageUrl: undefined })
              }
              onNext={handleNext}
              onBack={handleBack}
              disableLength={disableLength}
            />
          );
        } else {
          // For non-power cables, show Finishing Selection
          return (
            <FinishingSelection
              value={config.finishing}
              onChange={(finishing) => setConfig({ ...config, finishing })}
              onNext={handleNext}
              onBack={handleBack}
              config={config}
              sku={generateSKU(config)}
            />
          );
        }
      case 6:
        // Finishing Selection (for power cables) or Review (for non-power cables)
        if (config.type === 'power') {
          return (
            <FinishingSelection
              value={config.finishing}
              onChange={(finishing) => setConfig({ ...config, finishing })}
              onNext={handleNext}
              onBack={handleBack}
              config={config}
              sku={generateSKU(config)}
            />
          );
        } else {
          // Review for non-power cables
          return (
            <ReviewCart
              config={config}
              cart={cart}
              onAddToCart={handleAddToCart}
              onRemoveFromCart={handleRemoveFromCart}
              onBack={handleBack}
              svgRef={svgRef}
              isEmbedded={isEmbedded}
            />
          );
        }
             case 7:
        // Review for power cables
        return (
          <ReviewCart
            config={config}
            cart={cart}
            onAddToCart={handleAddToCart}
            onRemoveFromCart={handleRemoveFromCart}
            onBack={handleBack}
            svgRef={svgRef}
            isEmbedded={isEmbedded}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img 
          src="/images/nlfx_professional_laser_copy_1539721471__09702.webp" 
          alt="NLFX Professional" 
          style={{ height: '40px' }}
        />
      </Box>
      <Divider />
      <List>
        <ListItem button>
          <ListItemIcon>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText primary="Home" />
        </ListItem>
        <ListItem button>
          <ListItemIcon>
            <BuildIcon />
          </ListItemIcon>
          <ListItemText primary="Cable Builder" />
        </ListItem>
        <ListItem button>
          <ListItemIcon>
            <CartIcon />
          </ListItemIcon>
          <ListItemText primary="Cart" />
        </ListItem>
        <ListItem button>
          <ListItemIcon>
            <InfoIcon />
          </ListItemIcon>
          <ListItemText primary="About" />
        </ListItem>
      </List>
    </Box>
  );

  const handleSaveConfig = () => {
    setSaveDialogOpen(true);
  };

  const handleConfigSaved = (hash) => {
    setUserSavedHashes(prev => {
      const updated = [...new Set([...prev, hash])];
      localStorage.setItem('userSavedHashes', JSON.stringify(updated));
      return updated;
    });
  };

  const isManager = user && (user.role === 'ADMIN' || user.role === 'MANAGER');

  const handleLoadConfigByHash = (hash) => {
    // Load config from localStorage
    const savedConfigs = JSON.parse(localStorage.getItem('savedConfigs') || '[]');
    const configData = savedConfigs.find(cfg => cfg.hash === hash);
    if (configData) {
      setConfig(configData.config);
      setMyConfigsOpen(false);
      setPublicConfigsOpen(false);
    } else {
      console.error('Config not found:', hash);
    }
  };

  const handleDeleteMyConfig = (hash) => {
    // Remove from localStorage
    const savedConfigs = JSON.parse(localStorage.getItem('savedConfigs') || '[]');
    const updatedConfigs = savedConfigs.filter(cfg => cfg.hash !== hash);
    localStorage.setItem('savedConfigs', JSON.stringify(updatedConfigs));
    
    setUserSavedHashes(prev => {
      const updated = prev.filter(h => h !== hash);
      localStorage.setItem('userSavedHashes', JSON.stringify(updated));
      return updated;
    });
  };

  const handleDeletePublicConfig = (hash) => {
    // Only allow if manager
    if (!isManager) return;
    
    // Remove from localStorage
    const savedConfigs = JSON.parse(localStorage.getItem('savedConfigs') || '[]');
    const updatedConfigs = savedConfigs.filter(cfg => cfg.hash !== hash);
    localStorage.setItem('savedConfigs', JSON.stringify(updatedConfigs));
    
    setPublicConfigsOpen(false); // Refresh list on next open
  };

  const handleClonePublicConfig = (cfg) => {
    // Save as a new config for the user using localStorage
    const timestamp = Date.now();
    const hash = btoa(`${cfg.name}-${timestamp}`).replace(/[^a-zA-Z0-9]/g, '').substring(0, 8);
    
    const clonedConfig = {
      hash,
      name: cfg.name + ' (Cloned)',
      config: cfg.config,
      public: false,
      timestamp,
      createdBy: 'local'
    };
    
    // Save to localStorage
    const savedConfigs = JSON.parse(localStorage.getItem('savedConfigs') || '[]');
    savedConfigs.push(clonedConfig);
    localStorage.setItem('savedConfigs', JSON.stringify(savedConfigs));
    
    setUserSavedHashes(prev => {
      const updated = [...new Set([...prev, hash])];
      localStorage.setItem('userSavedHashes', JSON.stringify(updated));
      return updated;
    });
    handleLoadConfigByHash(hash);
  };

  const loadTawkToChat = () => {
    // Check if Tawk.to is already loaded
    if (window.Tawk_API) {
      window.Tawk_API.maximize();
      return;
    }

    // Load Tawk.to script dynamically
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://embed.tawk.to/6840bde338f349190a13ea27/1isuebgjn';
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    
    // Once loaded, maximize the chat
    script.onload = () => {
      if (window.Tawk_API) {
        window.Tawk_API.maximize();
      }
    };
    
    document.head.appendChild(script);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: '100vh', bgcolor: nlfxColors.background, ...(isEmbedded ? { overflow: 'hidden', height: '100vh' } : {}) }}>
      <AppBar 
        position="static" 
        sx={{ 
          bgcolor: nlfxColors.primary,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1,
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <a
              href="https://www.nlfxpro.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <img 
                src="/images/nlfx_professional_laser_copy_1539721471__09702.webp" 
                alt="NLFX Professional" 
                style={{ 
                  height: isMobile ? '32px' : '40px',
                  marginRight: isMobile ? '8px' : '16px'
                }}
              />
            </a>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  color: nlfxColors.text,
                  fontWeight: 600,
                  fontSize: isMobile ? '1rem' : '1.25rem',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Cable Builder
              </Typography>
            </Link>
            {user && hasPermission('manage_pricing') && (
              <Button 
                color="inherit" 
                sx={{ ml: 2, border: '1px solid #fff', borderRadius: 1 }}
                onClick={() => window.location.href = '/admin'}
              >
                Admin Dashboard
              </Button>
            )}
          </Box>
          
          {/* Help Menu Button */}
          <Button
            color="inherit"
            onClick={(e) => setHelpAnchorEl(e.currentTarget)}
            sx={{
              mr: 1,
              '&:hover': {
                color: nlfxColors.secondary
              }
            }}
          >
            Need Help?
          </Button>

          {/* Configs Menu Button */}
          <Button
            color="inherit"
            onClick={(e) => setConfigsAnchorEl(e.currentTarget)}
            sx={{
              mr: 2,
              '&:hover': {
                color: nlfxColors.secondary
              }
            }}
          >
            Configs
          </Button>

          {/* Cart Button - Hidden in embedded mode */}
          {!isEmbedded && (
            <Tooltip title={`Cart (${cart.length} items)`}>
              <IconButton
                color="inherit"
                onClick={() => setCartDialogOpen(true)}
                sx={{
                  mr: 2,
                  position: 'relative',
                  '&:hover': {
                    color: nlfxColors.secondary
                  }
                }}
              >
                <CartIcon />
                {cart.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      minWidth: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: nlfxColors.secondary,
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: 'translate(50%, -50%)'
                    }}
                  >
                    {cart.length}
                  </Box>
                )}
              </IconButton>
            </Tooltip>
          )}
          
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mr: 2, 
                  color: nlfxColors.text,
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {user.username}
              </Typography>
              <IconButton 
                color="inherit" 
                onClick={handleLogout}
                sx={{ 
                  '&:hover': {
                    color: nlfxColors.secondary
                  }
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Box>
          ) : (
            <Button 
              color="inherit" 
              onClick={() => setLoginDialogOpen(true)}
              startIcon={<AccountCircle />}
              sx={{ 
                '&:hover': {
                  color: nlfxColors.secondary
                }
              }}
            >
              {isMobile ? '' : 'Login'}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {!isEmbedded && (
        <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 250,
            bgcolor: nlfxColors.primary,
            color: nlfxColors.text
          }
        }}
      >
        {drawer}
        </Drawer>
      )}

      {/* Scrollable content area: on mobile/touch this is the scroll container so touch scrolling works inside the builder viewport (and in iframes). Class nlfx-scroll-container is excluded from mobileScrollService overflow:visible so it can scroll when embedded. */}
      <Box
        ref={scrollContainerRef}
        className="nlfx-scroll-container"
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: (isEmbedded ? 'auto' : { xs: 'auto', sm: 'visible' }),
          WebkitOverflowScrolling: 'touch',
          overflowX: 'hidden',
        }}
      >
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: isEmbedded ? { xs: 0, sm: 2 } : { xs: 2, sm: 4 },
          pt: isEmbedded ? { xs: 0, sm: 2 } : undefined,
          px: isEmbedded ? { xs: 1, sm: 2 } : { xs: 2, sm: 3 },
          position: 'relative',
          flex: 1,
        }}
      >
        {/* Remove duplicate header/subheader here. Only render in getStepContent. */}
        {/* Render the compatibility preview panel if enabled */}
        
        {/* Real-Time Price Estimator - Desktop */}
        {/* Temporarily disabled
        <RealTimePriceEstimator config={config} showBreakdown={true} defaultMinimized={true} />
        */}

        {/* Mobile Price Estimator - Temporarily disabled for all devices */}
        {/* <Box sx={{ 
          display: { xs: 'block', lg: 'none' },
          mb: 3
        }}>
          <RealTimePriceEstimator config={config} showBreakdown={false} />
        </Box> */}


        <Box sx={{ 
          width: '100%', 
          mb: { xs: 2, sm: 4 },
          '& .MuiStepLabel-label': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }
        }}>
          {/* On mobile, horizontal scroll so "Cable Type" and all steps stay in view */}
          <Box sx={{ 
            overflowX: 'auto', 
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            px: { xs: 1.5, sm: 0 },
            mx: { xs: -1.5, sm: 0 },
            '&::-webkit-scrollbar': { height: 6 }
          }}>
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel
              sx={{
              minWidth: { xs: 520, sm: 'auto' },
              '& .MuiStepLabel-iconContainer': {
                '& .MuiStepIcon-root': {
                  color: nlfxColors.secondary,
                  '&.Mui-active': {
                    color: nlfxColors.secondary
                  },
                  '&.Mui-completed': {
                    color: nlfxColors.secondary
                  }
                }
              },
              // Mobile-specific stepper enhancements
              '& .MuiStepLabel-root': {
                cursor: { xs: 'pointer', sm: 'default' },
                '&:hover': {
                  '& .MuiStepIcon-root': {
                    transform: { xs: 'scale(1.1)', sm: 'none' },
                    transition: { xs: 'transform 0.2s ease', sm: 'none' }
                  }
                }
              }
            }}
          >
            <Step key="Cable Type">
              <StepLabel 
                onClick={() => handleStepClick(0)}
                sx={{
                  cursor: activeStep > 0 ? 'pointer' : 'default',
                  '&:hover': activeStep > 0 ? {
                    '& .MuiStepIcon-root': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  } : {}
                }}
              >
                Cable Type
              </StepLabel>
            </Step>
            <Step key="Style">
              <StepLabel 
                onClick={() => handleStepClick(1)}
                sx={{
                  cursor: activeStep > 1 ? 'pointer' : 'default',
                  '&:hover': activeStep > 1 ? {
                    '& .MuiStepIcon-root': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  } : {}
                }}
              >
                Style
              </StepLabel>
            </Step>
            <Step key="Connectors">
              <StepLabel 
                onClick={() => handleStepClick(2)}
                sx={{
                  cursor: activeStep > 2 ? 'pointer' : 'default',
                  '&:hover': activeStep > 2 ? {
                    '& .MuiStepIcon-root': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  } : {}
                }}
              >
                Connectors
              </StepLabel>
            </Step>
            {config.type === 'power' && (
              <Step key="Wire Gauge">
                <StepLabel 
                  onClick={() => handleStepClick(3)}
                  sx={{
                    cursor: activeStep > 3 ? 'pointer' : 'default',
                    '&:hover': activeStep > 3 ? {
                      '& .MuiStepIcon-root': {
                        transform: 'scale(1.1)',
                        transition: 'transform 0.2s ease-in-out'
                      }
                    } : {}
                  }}
                >
                  Wire Gauge
                </StepLabel>
              </Step>
            )}
            <Step key="Length">
              <StepLabel 
                onClick={() => handleStepClick(config.type === 'power' ? 4 : 3)}
                sx={{
                  cursor: activeStep > (config.type === 'power' ? 4 : 3) ? 'pointer' : 'default',
                  '&:hover': activeStep > (config.type === 'power' ? 4 : 3) ? {
                    '& .MuiStepIcon-root': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  } : {}
                }}
              >
                Length
              </StepLabel>
            </Step>
            <Step key="Finishing">
              <StepLabel 
                onClick={() => handleStepClick(config.type === 'power' ? 5 : 4)}
                sx={{
                  cursor: activeStep > (config.type === 'power' ? 5 : 4) ? 'pointer' : 'default',
                  '&:hover': activeStep > (config.type === 'power' ? 5 : 4) ? {
                    '& .MuiStepIcon-root': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  } : {}
                }}
              >
                Finishing
              </StepLabel>
            </Step>
            <Step key="Review">
              <StepLabel 
                onClick={() => handleStepClick(config.type === 'power' ? 6 : 5)}
                sx={{
                  cursor: activeStep > (config.type === 'power' ? 6 : 5) ? 'pointer' : 'default',
                  '&:hover': activeStep > (config.type === 'power' ? 6 : 5) ? {
                    '& .MuiStepIcon-root': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  } : {}
                }}
              >
                Review
              </StepLabel>
            </Step>
          </Stepper>
          </Box>
        </Box>

        {/* Mobile Loading Indicator */}
        {isLoading && isMobile && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 4,
            gap: 2
          }}>
            <CircularProgress 
              size={40} 
              sx={{ color: nlfxColors.secondary }} 
            />
            <Typography variant="body2" color="text.secondary">
              Loading NLFX Cable Builder...
            </Typography>
            {!imagesLoaded && (
              <Typography variant="caption" color="text.secondary">
                Optimizing for mobile
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ 
          mt: { xs: 2, sm: 4 },
          bgcolor: nlfxColors.card,
          borderRadius: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          p: { xs: 2, sm: 3 },
          position: 'relative',
          // Mobile-specific enhancements
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            // Lazy loading for mobile
            loading: 'lazy'
          },
          // Enhanced touch interactions for mobile
          '& .MuiButton-root, & .MuiIconButton-root': {
            touchAction: 'manipulation', // Optimize touch handling
            WebkitTapHighlightColor: 'transparent' // Remove default tap highlight
          }
        }}>
          {getStepContent(activeStep)}
        </Box>

        <Box 
          {...swipeHandlers}
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 1,
            mt: { xs: 2, sm: 4 },
            pb: isEmbedded ? { xs: 4, sm: 2 } : 0,
            // Mobile-specific touch optimizations
            '& .MuiButton-root': {
              minHeight: { xs: '48px', sm: '36px' }, // Larger touch targets on mobile
              minWidth: { xs: '80px', sm: '64px' },
              fontSize: { xs: '1rem', sm: '0.875rem' },
              // Enhanced touch feedback
              '&:active': {
                transform: { xs: 'scale(0.95)', sm: 'none' },
                transition: { xs: 'transform 0.1s ease', sm: 'none' }
              }
            }
          }}
        >
          {/* On mobile, show missing requirements inline so users know what to do next */}
          {isMobile && !canProceed && missingRequirements.length > 0 && ((config.type === 'power' && activeStep < 6) || (config.type !== 'power' && activeStep < 5)) && (
            <Alert severity="info" sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Complete the following to continue:
              </Typography>
              <List dense disablePadding sx={{ listStyle: 'disc', pl: 2 }}>
                {missingRequirements.map((req, index) => (
                  <ListItem key={index} disablePadding sx={{ display: 'list-item', minHeight: 'auto', py: 0.25 }}>
                    <ListItemText primary={req} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
            sx={{
              borderColor: nlfxColors.secondary,
              color: nlfxColors.secondary,
              '&:hover': {
                borderColor: nlfxColors.secondary,
                bgcolor: 'rgba(179, 43, 43, 0.04)'
              },
              // Mobile-specific enhancements
              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed'
              }
            }}
          >
            Back
          </Button>
          {/* Show NEXT for all steps except the last two, REVIEW for the Review step, RESET for after Add to Cart */}
          {((config.type === 'power' && activeStep < 6) || (config.type !== 'power' && activeStep < 5)) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Tooltip also wraps the NEXT button when disabled so it shows on hover/click */}
              <Tooltip
                title={
                  !canProceed && missingRequirements.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Please complete the following:
                      </Typography>
                      <List dense sx={{ p: 0 }}>
                        {missingRequirements.map((req, index) => (
                          <ListItem key={index} sx={{ p: 0, minHeight: 'auto' }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <WarningIcon fontSize="small" color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary={req}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontSize: '0.875rem',
                                  color: '#fff'
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ) : ''
                }
                arrow
                placement="top"
                disableFocusListener={canProceed || missingRequirements.length === 0}
                disableHoverListener={canProceed || missingRequirements.length === 0}
                disableTouchListener={canProceed || missingRequirements.length === 0}
                open={!canProceed && missingRequirements.length > 0 ? nextTooltipOpen : false}
                onClose={() => setNextTooltipOpen(false)}
              >
                {/* span wrapper allows tooltip on disabled button */}
                <span
                  onMouseEnter={() => {
                    if (!canProceed && missingRequirements.length > 0) setNextTooltipOpen(true);
                  }}
                  onMouseLeave={() => setNextTooltipOpen(false)}
                  onClick={(e) => {
                    if (!canProceed && missingRequirements.length > 0) {
                      e.preventDefault();
                      setNextTooltipOpen(true);
                    }
                  }}
                  style={{ display: 'inline-flex' }}
                >
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    color="primary"
                    disabled={!canProceed}
                    sx={{
                      bgcolor: nlfxColors.secondary,
                      color: '#fff',
                      '&:hover': {
                        bgcolor: '#8b2222'
                      },
                      // Mobile-specific enhancements
                      '&:disabled': {
                        bgcolor: '#ccc',
                        color: '#666',
                        ...( !canProceed && missingRequirements.length > 0 ? {
                          border: `2px solid ${nlfxColors.secondary}`,
                          boxShadow: `0 0 0 2px ${nlfxColors.secondary} inset`
                        } : {})
                      }
                    }}
                  >
                    Next
                  </Button>
                </span>
              </Tooltip>
              {!canProceed && missingRequirements.length > 0 && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Please complete the following:
                      </Typography>
                      <List dense sx={{ p: 0 }}>
                        {missingRequirements.map((req, index) => (
                          <ListItem key={index} sx={{ p: 0, minHeight: 'auto' }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <WarningIcon fontSize="small" color="warning" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={req} 
                              sx={{ 
                                '& .MuiListItemText-primary': { 
                                  fontSize: '0.875rem',
                                  color: '#fff'
                                } 
                              }} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    sx={{
                      color: nlfxColors.secondary,
                      '&:hover': {
                        bgcolor: 'rgba(179, 43, 43, 0.1)'
                      }
                    }}
                    onClick={() => setNextTooltipOpen(true)}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          {((config.type === 'power' && activeStep === 6) || (config.type !== 'power' && activeStep === 5)) && (
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{
                bgcolor: nlfxColors.secondary,
                color: '#fff',
                '&:hover': {
                  bgcolor: '#8b2222'
                }
              }}
            >
              Review
            </Button>
          )}
          {((config.type === 'power' && activeStep === 7) || (config.type !== 'power' && activeStep === 6)) && (
            <Button 
              onClick={handleReset} 
              variant="outlined"
              sx={{
                borderColor: nlfxColors.secondary,
                color: nlfxColors.secondary,
                '&:hover': {
                  borderColor: nlfxColors.secondary,
                  bgcolor: 'rgba(179, 43, 43, 0.04)'
                }
              }}
            >
              {hasAddedToCart ? 'Build Another' : 'Reset'}
            </Button>
          )}
          </Box>
        </Box>
      </Container>
      </Box>

      {/* Help Menu */}
      <Menu
        anchorEl={helpAnchorEl}
        open={Boolean(helpAnchorEl)}
        onClose={() => setHelpAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: 2,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={() => { setQuickStartOpen(true); setHelpAnchorEl(null); }}>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Quick Start Guide</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setFaqOpen(true); setHelpAnchorEl(null); }}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>FAQ</ListItemText>
        </MenuItem>

      </Menu>

      {/* Configs Menu */}
      <Menu
        anchorEl={configsAnchorEl}
        open={Boolean(configsAnchorEl)}
        onClose={() => setConfigsAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: 2,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={() => { handleSaveConfig(); setConfigsAnchorEl(null); }}>
          <ListItemIcon>
            <StarIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save Current Config</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMyConfigsOpen(true); setConfigsAnchorEl(null); }}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Saved Configs</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setPublicConfigsOpen(true); setConfigsAnchorEl(null); }}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Public Configs</ListItemText>
        </MenuItem>
        {user && (
          <MenuItem onClick={() => { setCompatibilityManagerOpen(true); setConfigsAnchorEl(null); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Compatibility Matrix</ListItemText>
          </MenuItem>
        )}
        {user && hasPermission('manage_pricing') && (
          <MenuItem onClick={() => { setCompatibilityMatrixAdminOpen(true); setConfigsAnchorEl(null); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Compatibility Matrix Admin</ListItemText>
          </MenuItem>
        )}
        {user && hasPermission('manage_pricing') && (
          <MenuItem onClick={() => { setPricingAdminOpen(true); setConfigsAnchorEl(null); }}>
            <ListItemIcon>
              <StarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Pricing Admin</ListItemText>
          </MenuItem>
        )}
        {user && hasPermission('manage_pricing') && (
          <MenuItem onClick={() => { setCompatibilityAdminOpen(true); setConfigsAnchorEl(null); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Compatibility Admin</ListItemText>
          </MenuItem>
        )}
        {user && hasPermission('manage_pricing') && (
          <MenuItem onClick={() => { setDiagramAdminOpen(true); setConfigsAnchorEl(null); }}>
            <ListItemIcon>
              <BuildIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cable Diagrams</ListItemText>
          </MenuItem>
        )}
        {user && hasPermission('view_analytics') && (
          <MenuItem onClick={() => { setLiveMonitoringOpen(true); setConfigsAnchorEl(null); }}>
            <ListItemIcon>
              <TimelineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Live Monitoring</ListItemText>
          </MenuItem>
        )}
        {user && hasPermission('view_analytics') && (
          <MenuItem onClick={() => { setAnalyticsDashboardOpen(true); setConfigsAnchorEl(null); }}>
            <ListItemIcon>
              <AssessmentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Analytics Dashboard</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <QuickStartGuideDialog open={quickStartOpen} onClose={() => setQuickStartOpen(false)} />
      <FAQDialog open={faqOpen} onClose={() => setFaqOpen(false)} />

      <Dialog 
        open={loginDialogOpen} 
        onClose={() => setLoginDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: { xs: '90%', sm: 400 }
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: nlfxColors.primary,
          color: nlfxColors.text,
          borderBottom: `1px solid ${nlfxColors.secondary}`
        }}>
          Login
        </DialogTitle>
        <form onSubmit={handleLogin}>
          <DialogContent sx={{ pt: 3 }}>
            {loginError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  bgcolor: 'rgba(211, 47, 47, 0.1)',
                  color: nlfxColors.error
                }}
              >
                {loginError}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Username"
              type="text"
              fullWidth
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: nlfxColors.secondary
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: nlfxColors.secondary
                }
              }}
            />
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: nlfxColors.secondary
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: nlfxColors.secondary
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setLoginDialogOpen(false)}
              sx={{
                color: nlfxColors.secondary,
                '&:hover': {
                  bgcolor: 'rgba(179, 43, 43, 0.04)'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loginLoading}
              sx={{
                bgcolor: nlfxColors.secondary,
                '&:hover': {
                  bgcolor: '#8b2222'
                }
              }}
            >
              {loginLoading ? <CircularProgress size={24} /> : 'Login'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <SaveConfigDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        config={config}
        onSaved={handleConfigSaved}
        isManager={isManager}
      />

      <MyConfigsDialog
        open={myConfigsOpen}
        onClose={() => setMyConfigsOpen(false)}
        hashes={userSavedHashes}
        onLoad={handleLoadConfigByHash}
        onDelete={handleDeleteMyConfig}
      />

      <PublicConfigsDialog
        open={publicConfigsOpen}
        onClose={() => setPublicConfigsOpen(false)}
        onLoad={handleLoadConfigByHash}
        isManager={isManager}
        onDelete={handleDeletePublicConfig}
        onClone={!isManager ? handleClonePublicConfig : undefined}
      />

      <CompatibilityMatrixManager
        open={compatibilityManagerOpen}
        onClose={() => setCompatibilityManagerOpen(false)}
      />

      <Dialog
        open={compatibilityMatrixAdminOpen}
        onClose={() => setCompatibilityMatrixAdminOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: nlfxColors.primary,
          color: nlfxColors.text,
          borderBottom: `1px solid ${nlfxColors.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            Compatibility Matrix Administration
          </Box>
          <IconButton
            onClick={() => setCompatibilityMatrixAdminOpen(false)}
            sx={{ color: nlfxColors.text }}
          >
            <Typography variant="h6">×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <CompatibilityMatrixAdmin />
        </DialogContent>
      </Dialog>

      <Dialog
        open={pricingAdminOpen}
        onClose={() => setPricingAdminOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: nlfxColors.primary,
          color: nlfxColors.text,
          borderBottom: `1px solid ${nlfxColors.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1 }} />
            Pricing Administration
          </Box>
          <IconButton
            onClick={() => setPricingAdminOpen(false)}
            sx={{ color: nlfxColors.text }}
          >
            <Typography variant="h6">×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <PricingAdmin />
        </DialogContent>
      </Dialog>

      <Dialog
        open={compatibilityAdminOpen}
        onClose={() => setCompatibilityAdminOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: nlfxColors.primary,
          color: nlfxColors.text,
          borderBottom: `1px solid ${nlfxColors.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            Compatibility Matrix Management
          </Box>
          <IconButton
            onClick={() => setCompatibilityAdminOpen(false)}
            sx={{ color: nlfxColors.text }}
          >
            <Typography variant="h6">×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <CompatibilityAdmin />
        </DialogContent>
      </Dialog>

      <Dialog
        open={diagramAdminOpen}
        onClose={() => setDiagramAdminOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: nlfxColors.primary,
          color: nlfxColors.text,
          borderBottom: `1px solid ${nlfxColors.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BuildIcon sx={{ mr: 1 }} />
            Cable Diagram Management
          </Box>
          <IconButton
            onClick={() => setDiagramAdminOpen(false)}
            sx={{ color: nlfxColors.text }}
          >
            <Typography variant="h6">×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <DiagramAdmin />
        </DialogContent>
      </Dialog>

      <Dialog
        open={liveMonitoringOpen}
        onClose={() => setLiveMonitoringOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: nlfxColors.primary,
          color: nlfxColors.text,
          borderBottom: `1px solid ${nlfxColors.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TimelineIcon sx={{ mr: 1 }} />
            Live Monitoring Dashboard
          </Box>
          <IconButton
            onClick={() => setLiveMonitoringOpen(false)}
            sx={{ color: nlfxColors.text }}
          >
            <Typography variant="h6">×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <LiveMonitoringDashboard />
        </DialogContent>
      </Dialog>

      {/* Analytics Dashboard Dialog */}
      <Dialog
        open={analyticsDashboardOpen}
        onClose={() => setAnalyticsDashboardOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: nlfxColors.primary,
          color: nlfxColors.text,
          borderBottom: `1px solid ${nlfxColors.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon sx={{ mr: 1 }} />
            Analytics Dashboard
          </Box>
          <IconButton
            onClick={() => setAnalyticsDashboardOpen(false)}
            sx={{ color: nlfxColors.text }}
          >
            <Typography variant="h6">×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <AnalyticsDashboard />
        </DialogContent>
      </Dialog>

      {/* Cart Dialog */}
      <Dialog
        open={cartDialogOpen}
        onClose={() => setCartDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: nlfxColors.primary,
          color: nlfxColors.text,
          borderBottom: `1px solid ${nlfxColors.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CartIcon sx={{ mr: 1 }} />
            Cart ({cart.length} items)
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {cart.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearCart}
                sx={{ 
                  color: nlfxColors.text, 
                  borderColor: nlfxColors.text,
                  '&:hover': {
                    borderColor: nlfxColors.secondary,
                    color: nlfxColors.secondary
                  }
                }}
              >
                Clear Cart
              </Button>
            )}
            <IconButton
              onClick={() => setCartDialogOpen(false)}
              sx={{ color: nlfxColors.text }}
            >
              <Typography variant="h6">×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <CartItemsView
            cart={cart}
            onRemoveFromCart={handleRemoveFromCart}
          />
        </DialogContent>
      </Dialog>

      <CartRestoreNotification 
        open={showCartRestoreNotification && cart.length > 0} 
        onClose={() => setShowCartRestoreNotification(false)}
        cartCount={cart.length}
      />
      
      {/* Loading Overlay for Puppeteer Capture */}
      <CableDesignLoadingOverlay
        open={showLoadingOverlay}
        progress={loadingProgress}
      />
    </Box>
  );
}

// Wrap App in Router and add routes
export default function WrappedApp() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/admin" element={<PricingAdmin />} />
        <Route path="/bom/:bomId" element={<BOMViewer />} />
        <Route path="/panel-builder" element={<PanelBuilder />} />
      </Routes>
    </Router>
  );
} 